import React, { useState } from 'react';
import '../csscomponent/Signup.css';
import eyeOpen from '../assest/eye.png';
import eyeClosed from '../assest/show.png';
import signupIllustration from '../assest/signup.png';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../lib/authSession';
import useSingleTapNavigate from '../lib/useSingleTapNavigate';
import LogoButtonLoader from './mobile/LogoButtonLoader';

const BUTTON_LOADER_MIN_MS = 150;

export default function Signup() {
  const navigate = useNavigate();
  const { goTo, navLocked } = useSingleTapNavigate();
  const { signup, loginGoogle } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);

  const holdLoader = () => new Promise((resolve) => {
    window.setTimeout(resolve, BUTTON_LOADER_MIN_MS);
  });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const getPasswordError = (password) => {
    if (!password.trim()) return 'Password is required';
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password) || password.length < 8) {
      return 'Use 8+ characters with uppercase, lowercase, and a number';
    }
    return '';
  };

  const validate = () => {
    const nextErrors = {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    };

    if (!formData.name.trim()) {
      nextErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      nextErrors.email = 'Enter a valid email address';
    }

    nextErrors.password = getPasswordError(formData.password);

    if (!formData.confirmPassword.trim()) {
      nextErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(nextErrors);

    const firstError = Object.values(nextErrors).find(Boolean);
    if (firstError) {
      toast.error(firstError);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const [result] = await Promise.all([
        signup({
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
        }),
        holdLoader(),
      ]);
      toast.success(result?.message || 'Account created successfully! Welcome.');
      const pendingCheckoutPath = sessionStorage.getItem('pendingCheckoutPath');
      if (pendingCheckoutPath) {
        sessionStorage.removeItem('pendingCheckoutPath');
        navigate(pendingCheckoutPath, { replace: true });
        return;
      }
      navigate('/home', { replace: true });
    } catch (error) {
      toast.error(error?.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setGoogleLoading(true);
      await Promise.all([
        loginGoogle(),
        holdLoader(),
      ]);
      const pendingCheckoutPath = sessionStorage.getItem('pendingCheckoutPath');
      if (pendingCheckoutPath) {
        sessionStorage.removeItem('pendingCheckoutPath');
        navigate(pendingCheckoutPath, { replace: true });
        return;
      }
      toast.success('Signed in with Google successfully.');
      navigate('/home', { replace: true });
    } catch (error) {
      toast.error(error?.message || 'Google sign up failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
  };

  return (
    <div className="signup-page-wrapper">
      <div className="signup-box">
        <div className="signup-content">
          <div className="signup-topbar">
            <button
              type="button"
              className="signup-skip-btn"
              onClick={() => goTo('/home')}
              title="Skip for now"
              disabled={navLocked}
            >
              Skip
            </button>
            <div className="signup-topbar-actions">
              <button
                type="button"
                className="signup-switch-btn"
                onClick={() => goTo('/login')}
                title="Back to Login"
                disabled={navLocked}
              >
                <span>Back to Login</span>
              </button>
            </div>
          </div>
          <h1>Create Account</h1>
          <form onSubmit={handleSubmit} noValidate>
            {/* Name */}
            <div className={`signup-input ${errors.name ? 'signup-error-input' : ''}`}>
              <label htmlFor="name">Full Name</label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
              />
              {errors.name && <p className="signup-error">{errors.name}</p>}
            </div>

            {/* Email */}
            <div className={`signup-input ${errors.email ? 'signup-error-input' : ''}`}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
              />
              {errors.email && <p className="signup-error">{errors.email}</p>}
            </div>

            {/* Password */}
            <div className={`signup-input ${errors.password ? 'signup-error-input' : ''}`}>
              <label htmlFor="password">Password</label>
              <div className="signup-password-field">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create password"
                />
                <img
                  src={showPassword ? eyeOpen : eyeClosed}
                  alt="toggle"
                  className="signup-eye-icon"
                  onClick={() => setShowPassword(!showPassword)}
                />
              </div>

              {/* Password rule check animation */}
              {formData.password && (
                <div className="password-conditions">
                  <p className={/[A-Z]/.test(formData.password) ? 'valid' : 'invalid'}>
                    {/[A-Z]/.test(formData.password) ? '✅' : '❌'} At least one uppercase (A–Z)
                  </p>
                  <p className={/[a-z]/.test(formData.password) ? 'valid' : 'invalid'}>
                    {/[a-z]/.test(formData.password) ? '✅' : '❌'} At least one lowercase (a–z)
                  </p>
                  <p className={/[0-9]/.test(formData.password) ? 'valid' : 'invalid'}>
                    {/[0-9]/.test(formData.password) ? '✅' : '❌'} At least one number (0–9)
                  </p>
                  <p className={formData.password.length >= 8 ? 'valid' : 'invalid'}>
                    {formData.password.length >= 8 ? '✅' : '❌'} Minimum 8 characters
                  </p>
                </div>
              )}

              {errors.password && <p className="signup-error">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div className={`signup-input ${errors.confirmPassword ? 'signup-error-input' : ''}`}>
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="signup-password-field">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                />
                <img
                  src={showConfirmPassword ? eyeOpen : eyeClosed}
                  alt="toggle"
                  className="signup-eye-icon"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                />
              </div>
              {errors.confirmPassword && <p className="signup-error">{errors.confirmPassword}</p>}
            </div>

            <button
              type="submit"
              className={`signup-submit-btn ${loading ? 'btn-loading' : ''}`}
              disabled={loading || googleLoading}
            >
              {loading ? (
                <LogoButtonLoader text="Creating account..." />
              ) : (
                'Sign Up'
              )}
            </button>

            <div className="signup-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className={`signup-google-btn ${googleLoading ? 'btn-loading' : ''}`}
              onClick={handleGoogleSignUp}
              disabled={loading || googleLoading}
              aria-label="Sign up with Google"
            >
              {googleLoading ? (
                <LogoButtonLoader text="Signing up with Google..." isDarkText />
              ) : (
                <>
                  <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                  <span>Sign up with Google</span>
                </>
              )}
            </button>
          </form>
        </div>
        <div className="signup-image">
          <img src={signupIllustration} alt="Signup" />
        </div>
      </div>
    </div>
  );
}
